import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Space, Popconfirm, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import {
  getPhoneBlacklist,
  addPhoneBlacklist,
  updatePhoneBlacklist,
  deletePhoneBlacklist,
  PhoneBlacklist,
} from '../../services/phoneBlacklist';

const { Title } = Typography;
const { TextArea } = Input;

const PhoneBlacklistPage: React.FC = () => {
  const [blacklist, setBlacklist] = useState<PhoneBlacklist[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<PhoneBlacklist | null>(null);
  const [keyword, setKeyword] = useState('');
  const [form] = Form.useForm();

  const fetchBlacklist = async () => {
    setLoading(true);
    try {
      const data = await getPhoneBlacklist({ keyword: keyword || undefined });
      setBlacklist(data);
    } catch (error) {
      message.error('获取黑名单失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlacklist();
  }, [keyword]);

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (item: PhoneBlacklist) => {
    setEditingItem(item);
    form.setFieldsValue({ remark: item.remark });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deletePhoneBlacklist(id);
      message.success('已解除拉黑');
      fetchBlacklist();
    } catch (error) {
      message.error('解除拉黑失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingItem) {
        // 更新备注
        await updatePhoneBlacklist(editingItem.id, { remark: values.remark });
        message.success('更新成功');
      } else {
        // 添加新黑名单
        await addPhoneBlacklist(values);
        message.success('添加成功');
      }
      
      setModalVisible(false);
      fetchBlacklist();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error(editingItem ? '更新失败' : '添加失败');
      }
    }
  };

  const columns = [
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
    },
    {
      title: '创建人',
      dataIndex: 'creator_name',
      key: 'creator_name',
      width: 120,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: PhoneBlacklist) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要解除拉黑吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              解除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4}>黑名单管理</Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加黑名单
          </Button>
        </div>

        <Input
          placeholder="搜索手机号或备注"
          prefix={<SearchOutlined />}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />

        <Table
          columns={columns}
          dataSource={blacklist}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
        />
      </Space>

      <Modal
        title={editingItem ? '编辑备注' : '添加黑名单'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          {!editingItem && (
            <Form.Item
              label="手机号"
              name="phone"
              rules={[
                { required: true, message: '请输入手机号' },
                { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' },
              ]}
            >
              <Input placeholder="请输入手机号" />
            </Form.Item>
          )}
          <Form.Item label="备注" name="remark">
            <TextArea rows={4} placeholder="请输入备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PhoneBlacklistPage;
